/**
 * Native Authentication Hook
 * 
 * Handles OAuth callback deep links in native apps.
 * Performs PKCE token exchange when auth redirects back to the app.
 */
import { useEffect, useCallback } from 'react';
import { Capacitor } from '@capacitor/core';
import { App, URLOpenListenerEvent } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import { debugLog } from '@/lib/debugLog';
import { buildApiUrl } from '@/config/api';
import { getBrowserTimeZone } from '@/lib/utils';

/**
 * Show a full-screen loading overlay during native auth flow.
 * This is injected directly into the DOM (not React) so it persists
 * through page reloads and React teardown/bootstrap.
 */
function showNativeAuthLoadingOverlay() {
  // Remove any existing overlay first
  const existing = document.getElementById('native-auth-loading');
  if (existing) existing.remove();

  const overlay = document.createElement('div');
  overlay.id = 'native-auth-loading';
  overlay.style.cssText = `
    position: fixed; inset: 0; z-index: 999999;
    display: flex; flex-direction: column;
    align-items: center; justify-content: center;
    background: linear-gradient(135deg, #eff6ff 0%, #e0e7ff 100%);
  `;
  overlay.innerHTML = `
    <style>
      @keyframes native-auth-spin {
        to { transform: rotate(360deg); }
      }
    </style>
    <svg style="width:48px;height:48px;animation:native-auth-spin 1s linear infinite;color:#2563eb;margin-bottom:16px" fill="none" viewBox="0 0 24 24">
      <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" opacity="0.25"></circle>
      <path fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
    </svg>
    <h2 style="font-size:1.25rem;font-weight:600;margin:0 0 8px;color:#1e293b;font-family:system-ui,sans-serif">Signing you in...</h2>
    <p style="font-size:0.95rem;color:#64748b;margin:0;font-family:system-ui,sans-serif">Take a breath and calm your senses</p>
  `;
  document.body.appendChild(overlay);

  // Set flag so the overlay also shows on page reload (index.html reads this)
  localStorage.setItem('native_auth_loading', 'true');
}

/**
 * Remove the native auth loading overlay.
 */
export function removeNativeAuthLoadingOverlay() {
  const el = document.getElementById('native-auth-loading');
  if (el) el.remove();
  localStorage.removeItem('native_auth_loading');
}

/**
 * Force Android WebView to repaint after returning from Chrome Custom Tab.
 * The WebView rendering surface can become stale, showing a blank white screen
 * even though the DOM is correct. This forces a visual refresh.
 */
export function forceWebViewRepaint() {
  // Method 1: Force a reflow + repaint via display toggle
  const body = document.body;
  if (!body) {
    return;
  }

  body.style.display = 'none';
  // Force browser to acknowledge the change (read triggers reflow)
  void body.offsetHeight;
  body.style.display = '';
  
  // Method 2: Schedule an opacity flicker for an additional frame-based repaint
  requestAnimationFrame(() => {
    body.style.opacity = '0.99';
    requestAnimationFrame(() => {
      body.style.opacity = '1';
    });
  });
}

/**
 * Persistent debug logger that stores to localStorage for later retrieval
 */
function persistentLog(msg: string, data?: unknown) {
  const logs = JSON.parse(localStorage.getItem('native_auth_debug') || '[]');
  logs.push({ time: new Date().toISOString(), msg, data });
  // Keep last 30 entries
  localStorage.setItem('native_auth_debug', JSON.stringify(logs.slice(-30)));
  debugLog(msg, data);
}

/**
 * Decode a JWT token without verifying (for extracting user info client-side)
 */
function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    
    // Decode base64url to base64
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    // Add padding if needed
    const padded = base64 + '='.repeat((4 - base64.length % 4) % 4);
    const decoded = atob(padded);
    return JSON.parse(decoded);
  } catch {
    return null;
  }
}

/**
 * Extract user info from Kinde tokens (id_token or access_token)
 */
function extractUserFromTokens(tokens: { id_token?: string; access_token?: string }): {
  userId: string;
  email: string;
  displayName: string | null;
  firstName: string | null;
  lastName: string | null;
  username: string;
  avatarUrl: string | null;
  isAdmin: boolean;
} | null {
  // Prefer id_token as it contains user profile info
  const idPayload = tokens.id_token ? decodeJwtPayload(tokens.id_token) : null;
  const accessPayload = tokens.access_token ? decodeJwtPayload(tokens.access_token) : null;
  
  // Get user ID from 'sub' claim
  const userId = (idPayload?.sub || accessPayload?.sub) as string | undefined;
  if (!userId) return null;
  
  // Extract user info from id_token
  const email = (idPayload?.email || '') as string;
  const givenName = (idPayload?.given_name || '') as string;
  const familyName = (idPayload?.family_name || '') as string;
  const picture = (idPayload?.picture || null) as string | null;
  
  // Build display name
  const displayName = [givenName, familyName].filter(Boolean).join(' ') || null;
  
  // Generate username from email or user ID
  const username = email ? email.split('@')[0] : userId.replace('kp:', '').substring(0, 8);
  
  return {
    userId,
    email,
    displayName,
    firstName: givenName || null,
    lastName: familyName || null,
    username,
    avatarUrl: picture,
    isAdmin: false, // Cannot determine admin status without backend
  };
}

// Get Kinde configuration (read lazily so runtime env changes are picked up)
const getKindeConfig = () => {
  const env = window.__ENV;
  return {
    clientId: env?.VITE_KINDE_CLIENT_ID ?? import.meta.env.VITE_KINDE_CLIENT_ID ?? '',
    domain: env?.VITE_KINDE_DOMAIN ?? import.meta.env.VITE_KINDE_DOMAIN ?? '',
  };
};

/**
 * Store tokens for native auth.
 * These are used by the app when the Kinde SDK isn't managing tokens.
 */
const NATIVE_TOKEN_KEY = 'native_auth_tokens';
// Use localStorage for PKCE state - sessionStorage is cleared on app restart from deep link
const PKCE_VERIFIER_KEY = 'native_auth_pkce_verifier';
const PKCE_STATE_KEY = 'native_auth_pkce_state';
const LAST_PROCESSED_CALLBACK_KEY = 'native_auth_last_processed_callback';
const CALLBACK_DEDUPE_WINDOW_MS = 2 * 60 * 1000;
const TOKEN_EXCHANGE_TIMEOUT_MS = 20000;
const BACKEND_SYNC_TIMEOUT_MS = 12000;
const REFRESH_TIMEOUT_MS = 10000;

interface ProcessedCallbackState {
  url: string;
  processedAt: number;
}

interface NativeTokens {
  access_token: string;
  refresh_token?: string;
  id_token?: string;
  expires_at: number;
}

function withTimeout<T>(promise: Promise<T>, timeoutMs: number, timeoutMessage: string): Promise<T> {
  return new Promise((resolve, reject) => {
    const timeoutId = window.setTimeout(() => reject(new Error(timeoutMessage)), timeoutMs);
    promise
      .then((value) => {
        window.clearTimeout(timeoutId);
        resolve(value);
      })
      .catch((error) => {
        window.clearTimeout(timeoutId);
        reject(error);
      });
  });
}

export function setNativeTokens(tokens: NativeTokens) {
  localStorage.setItem(NATIVE_TOKEN_KEY, JSON.stringify(tokens));
}

export function getNativeTokens(): NativeTokens | null {
  try {
    const stored = localStorage.getItem(NATIVE_TOKEN_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
}

export function clearNativeTokens() {
  localStorage.removeItem(NATIVE_TOKEN_KEY);
}

export function isNativeTokenValid(): boolean {
  const tokens = getNativeTokens();
  if (!tokens) return false;
  // Consider valid if access token is fresh OR a refresh token exists (session recoverable)
  return tokens.expires_at > Date.now() || !!tokens.refresh_token;
}

/**
 * Check if the native access token is expired or will expire within the given buffer (ms).
 * Does NOT consider refresh tokens — purely checks the access token lifetime.
 */
export function isNativeAccessTokenExpiringSoon(bufferMs = 5 * 60 * 1000): boolean {
  const tokens = getNativeTokens();
  if (!tokens) return true;
  return tokens.expires_at - Date.now() < bufferMs;
}

/**
 * Mutex to prevent concurrent refresh attempts.
 * If a refresh is already in progress, subsequent callers wait for the same promise.
 */
let refreshPromise: Promise<boolean> | null = null;

/**
 * Refresh native tokens using Kinde's token endpoint with grant_type=refresh_token.
 * 
 * - On success: stores new access_token, new refresh_token (Kinde rotates them), new expires_at
 * - On failure: clears tokens (caller should redirect to login)
 * - Uses a mutex so concurrent calls share the same in-flight request
 * 
 * Returns true if tokens were successfully refreshed, false otherwise.
 */
export async function refreshNativeTokens(): Promise<boolean> {
  // If a refresh is already in progress, wait for it
  if (refreshPromise) {
    return refreshPromise;
  }

  refreshPromise = _doRefreshNativeTokens();
  try {
    return await refreshPromise;
  } finally {
    refreshPromise = null;
  }
}

async function _doRefreshNativeTokens(): Promise<boolean> {
  const tokens = getNativeTokens();
  if (!tokens?.refresh_token) {
    persistentLog('[NativeAuth] No refresh token available, cannot refresh');
    return false;
  }

  const { clientId, domain } = getKindeConfig();
  if (!domain || !clientId) {
    persistentLog('[NativeAuth] Missing Kinde config for refresh', { domain: !!domain, clientId: !!clientId });
    return false;
  }

  try {
    const tokenUrl = `${domain}/oauth2/token`;
    persistentLog('[NativeAuth] Refreshing tokens via Kinde', tokenUrl);

    const response = await withTimeout(fetch(tokenUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: new URLSearchParams({
        grant_type: 'refresh_token',
        client_id: clientId,
        refresh_token: tokens.refresh_token,
      }),
    }), REFRESH_TIMEOUT_MS, '[NativeAuth] Refresh request timed out');

    if (!response.ok) {
      const errorText = await response.text();
      persistentLog('[NativeAuth] Token refresh failed', { status: response.status, error: errorText });
      // If the refresh token is invalid/expired, clear everything
      if (response.status === 400 || response.status === 401 || response.status === 403) {
        clearNativeTokens();
      }
      return false;
    }

    const newTokens = await response.json();
    persistentLog('[NativeAuth] Token refresh successful', {
      hasAccessToken: !!newTokens.access_token,
      hasRefreshToken: !!newTokens.refresh_token,
      expiresIn: newTokens.expires_in,
    });

    const updatedNativeTokens: NativeTokens = {
      access_token: newTokens.access_token,
      // Kinde rotates refresh tokens — use new one if provided, else keep old
      refresh_token: newTokens.refresh_token || tokens.refresh_token,
      id_token: newTokens.id_token || tokens.id_token,
      expires_at: Date.now() + (newTokens.expires_in * 1000),
    };
    setNativeTokens(updatedNativeTokens);

    return true;
  } catch (error) {
    const errorMsg = error instanceof Error ? error.message : String(error);
    persistentLog('[NativeAuth] Token refresh error', { error: errorMsg });
    return false;
  }
}

// PKCE state helpers - use localStorage to persist across app restarts
export function setPKCEState(codeVerifier: string, state: string) {
  localStorage.setItem(PKCE_VERIFIER_KEY, codeVerifier);
  localStorage.setItem(PKCE_STATE_KEY, state);
}

export function getPKCEState(): { codeVerifier: string | null; state: string | null } {
  return {
    codeVerifier: localStorage.getItem(PKCE_VERIFIER_KEY),
    state: localStorage.getItem(PKCE_STATE_KEY),
  };
}

export function clearPKCEState() {
  localStorage.removeItem(PKCE_VERIFIER_KEY);
  localStorage.removeItem(PKCE_STATE_KEY);
}

/**
 * Returns true when a callback URL should be processed, false for duplicate delivery.
 * Android can deliver the same callback through both getLaunchUrl() and appUrlOpen.
 */
export function shouldProcessNativeAuthCallback(url: string, now = Date.now()): boolean {
  try {
    const raw = localStorage.getItem(LAST_PROCESSED_CALLBACK_KEY);
    if (raw) {
      const last = JSON.parse(raw) as ProcessedCallbackState;
      const isDuplicateWithinWindow =
        last.url === url && now - last.processedAt < CALLBACK_DEDUPE_WINDOW_MS;

      if (isDuplicateWithinWindow) {
        return false;
      }
    }

    const next: ProcessedCallbackState = { url, processedAt: now };
    localStorage.setItem(LAST_PROCESSED_CALLBACK_KEY, JSON.stringify(next));
    return true;
  } catch {
    // If localStorage parse fails, fail-open and keep the auth flow moving.
    return true;
  }
}

/**
 * Hook to handle native OAuth callback flow
 * 
 * Listens for auth callback deep links, closes the browser,
 * and exchanges the authorization code for tokens.
 */
export function useNativeAuth() {
  /**
   * Exchange authorization code for tokens using PKCE
   */
  const exchangeCodeForTokens = useCallback(async (code: string, state: string) => {
    const { clientId, domain } = getKindeConfig();
    const { codeVerifier, state: savedState } = getPKCEState();
    
    persistentLog('[NativeAuth] Starting token exchange', { 
      hasVerifier: !!codeVerifier, 
      hasState: !!savedState,
      stateMatch: state === savedState,
      domain,
      hasClientId: !!clientId
    });
    
    // Validate state to prevent CSRF
    if (state !== savedState) {
      persistentLog('[NativeAuth] State mismatch', { got: state, expected: savedState });
      window.location.href = '/login?error=state_mismatch';
      return false;
    }
    
    if (!codeVerifier) {
      persistentLog('[NativeAuth] No code verifier found');
      window.location.href = '/login?error=pkce_lost';
      return false;
    }
    
    if (!domain || !clientId) {
      persistentLog('[NativeAuth] Missing Kinde config', { domain, clientId: !!clientId });
      window.location.href = '/login?error=config_missing';
      return false;
    }
    
    try {
      // Call Kinde directly - they support CORS for token endpoints
      const tokenUrl = `${domain}/oauth2/token`;
      persistentLog('[NativeAuth] Fetching token from Kinde', tokenUrl);
      
      const tokenResponse = await withTimeout(fetch(tokenUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          grant_type: 'authorization_code',
          client_id: clientId,
          code,
          code_verifier: codeVerifier,
          redirect_uri: 'cleariest://auth/callback',
        }),
      }), TOKEN_EXCHANGE_TIMEOUT_MS, '[NativeAuth] Token exchange request timed out');
      
      if (!tokenResponse.ok) {
        const errorText = await tokenResponse.text();
        persistentLog('[NativeAuth] Token exchange failed', { status: tokenResponse.status, error: errorText });
        clearPKCEState();
        window.location.href = '/login?error=token_exchange_failed&details=' + encodeURIComponent(errorText.substring(0, 100));
        return false;
      }
      
      const tokens = await tokenResponse.json();
      persistentLog('[NativeAuth] Token exchange successful', { 
        hasAccessToken: !!tokens.access_token,
        hasRefreshToken: !!tokens.refresh_token,
        expiresIn: tokens.expires_in
      });
      
      // Store tokens for native auth
      const nativeTokens: NativeTokens = {
        access_token: tokens.access_token,
        refresh_token: tokens.refresh_token,
        id_token: tokens.id_token,
        expires_at: Date.now() + (tokens.expires_in * 1000),
      };
      setNativeTokens(nativeTokens);

      // Store a fallback user immediately so AuthContext can bootstrap even if backend sync is slow.
      const jwtFallbackUser = extractUserFromTokens(tokens);
      if (jwtFallbackUser) {
        localStorage.setItem('user', JSON.stringify(jwtFallbackUser));
      }
      
      // Clean up PKCE state
      clearPKCEState();
      
      // Sync with backend to create/update user record
      // This is non-blocking - if it fails, we still proceed with login
      // The user will be synced on next app load via AuthContext
      try {
        const syncUrl = buildApiUrl('/api/auth/kinde/sync');
        persistentLog('[NativeAuth] Syncing with backend at', syncUrl);
        
        // Extract user info from tokens for the sync request
        const userInfo = extractUserFromTokens(tokens);
        if (!userInfo) {
          persistentLog('[NativeAuth] Could not extract user info from tokens for sync');
          throw new Error('Could not extract user info from tokens');
        }
        
        // Build sync request body as expected by the backend
        const syncBody = {
          kindeUserId: userInfo.userId,
          email: userInfo.email,
          fullName: userInfo.displayName,
          firstName: userInfo.firstName,
          lastName: userInfo.lastName,
          avatarUrl: userInfo.avatarUrl,
          timeZone: getBrowserTimeZone(),
        };
        persistentLog('[NativeAuth] Sync request body', syncBody);
        
        const syncResponse = await withTimeout(fetch(syncUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${tokens.access_token}`,
          },
          body: JSON.stringify(syncBody),
        }), BACKEND_SYNC_TIMEOUT_MS, '[NativeAuth] Backend sync request timed out');
        
        if (syncResponse.ok) {
          const syncData = await syncResponse.json();
          persistentLog('[NativeAuth] Backend sync successful', { userId: syncData.userId, email: syncData.email });
          
          // Backend returns different fields - map to our user format
          const userData = {
            userId: syncData.userId,
            username: syncData.username,
            email: syncData.email,
            displayName: syncData.displayName,
            firstName: syncData.firstName ?? null,
            lastName: syncData.lastName ?? null,
            avatarUrl: syncData.avatarUrl,
            isAdmin: syncData.isAdmin || false,
            showEmailToMembers: syncData.showEmailToMembers ?? false,
          };
          
          // Store user data for AuthContext
          localStorage.setItem('user', JSON.stringify(userData));
        } else {
          const errorText = await syncResponse.text();
          persistentLog('[NativeAuth] Backend sync error', { status: syncResponse.status, error: errorText });
          
          // Fallback: Extract user info from JWT tokens
          const fallbackUser = extractUserFromTokens(tokens);
          if (fallbackUser) {
            persistentLog('[NativeAuth] Using fallback user from JWT', { userId: fallbackUser.userId, email: fallbackUser.email });
            localStorage.setItem('user', JSON.stringify(fallbackUser));
          } else {
            persistentLog('[NativeAuth] Could not extract user from JWT');
          }
        }
      } catch (syncError) {
        // Network error during sync - try to extract user from JWT
        persistentLog('[NativeAuth] Backend sync network error', String(syncError));
        
        const fallbackUser = extractUserFromTokens(tokens);
        if (fallbackUser) {
          persistentLog('[NativeAuth] Using fallback user from JWT (network error)', { userId: fallbackUser.userId, email: fallbackUser.email });
          localStorage.setItem('user', JSON.stringify(fallbackUser));
        }
      }
      
      // Reload the app to reinitialize
      // The loading overlay is already visible from processAuthCallback,
      // and the localStorage flag ensures it persists through the page reload
      persistentLog('[NativeAuth] Auth complete, navigating to home...');
      window.location.href = '/';
      
      return true;
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : String(error);
      persistentLog('[NativeAuth] Token exchange error', { error: errorMsg });
      // Store error for debugging
      localStorage.setItem('native_auth_last_error', JSON.stringify({
        time: new Date().toISOString(),
        error: errorMsg,
        stack: error instanceof Error ? error.stack : undefined
      }));
      clearPKCEState();
      window.location.href = '/login?error=exchange_error&msg=' + encodeURIComponent(errorMsg.substring(0, 100));
      return false;
    }
  }, []);
  
  /**
   * Process auth callback URL (shared between cold start and deep link events)
   */
  const processAuthCallback = useCallback(async (url: string) => {
    persistentLog('[NativeAuth] Processing auth callback', url);

    if (!shouldProcessNativeAuthCallback(url)) {
      persistentLog('[NativeAuth] Duplicate auth callback ignored', url);
      return;
    }
    
    try {
      // Close the in-app browser
      await Browser.close();
      persistentLog('[NativeAuth] In-app browser closed');
    } catch (error) {
      // Browser might already be closed
      persistentLog('[NativeAuth] Browser close error (may already be closed)', String(error));
    }
    
    // Wait for Android WebView to re-acquire its rendering surface after
    // Chrome Custom Tab closes. Without this delay, the WebView may show
    // a blank white screen because its display surface is stale.
    await new Promise(resolve => setTimeout(resolve, 500));
    forceWebViewRepaint();
    
    // Show loading overlay immediately so the user sees the spinner
    // instead of a blank white screen during token exchange + app reload
    showNativeAuthLoadingOverlay();
    persistentLog('[NativeAuth] Loading overlay shown after browser close');
    
    // Parse the callback URL for the authorization code
    // Use a more robust parsing approach for custom schemes
    let code: string | null = null;
    let state: string | null = null;
    let error: string | null = null;
    let errorDescription: string | null = null;
    
    try {
      // Try standard URL parsing first
      const urlObj = new URL(url);
      code = urlObj.searchParams.get('code');
      state = urlObj.searchParams.get('state');
      error = urlObj.searchParams.get('error');
      errorDescription = urlObj.searchParams.get('error_description');
      persistentLog('[NativeAuth] URL parsed with standard parser');
    } catch {
      // Fallback: manual query string parsing for custom schemes
      persistentLog('[NativeAuth] Standard URL parsing failed, using fallback');
      const queryStart = url.indexOf('?');
      if (queryStart !== -1) {
        const queryString = url.substring(queryStart + 1);
        const params = new URLSearchParams(queryString);
        code = params.get('code');
        state = params.get('state');
        error = params.get('error');
        errorDescription = params.get('error_description');
      }
    }
    
    persistentLog('[NativeAuth] Parsed params', { 
      hasCode: !!code, 
      codeLength: code?.length,
      hasState: !!state, 
      error, 
      errorDescription 
    });
    
    if (error) {
      persistentLog('[NativeAuth] OAuth error from provider', { error, errorDescription });
      window.location.href = '/login?error=' + encodeURIComponent(error);
      return;
    }
    
    if (code && state) {
      await exchangeCodeForTokens(code, state);
    } else {
      persistentLog('[NativeAuth] Missing code or state in callback', { url });
      window.location.href = '/login?error=missing_params';
    }
  }, [exchangeCodeForTokens]);

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    
    persistentLog('[NativeAuth] Hook initialized on platform', Capacitor.getPlatform());
    
    // Check if app was cold-started from a deep link
    const checkLaunchUrl = async () => {
      try {
        persistentLog('[NativeAuth] Checking for launch URL...');
        const launchUrl = await App.getLaunchUrl();
        persistentLog('[NativeAuth] Launch URL result', { 
          hasUrl: !!launchUrl?.url, 
          url: launchUrl?.url?.substring(0, 100) 
        });
        
        if (launchUrl?.url && launchUrl.url.includes('auth/callback')) {
          persistentLog('[NativeAuth] Cold start with auth callback URL:', launchUrl.url);
          await processAuthCallback(launchUrl.url);
        }
      } catch (e) {
        persistentLog('[NativeAuth] Error checking launch URL', String(e));
      }
    };
    
    checkLaunchUrl();

    // Listen for deep links while app is running
    persistentLog('[NativeAuth] Setting up appUrlOpen listener');
    const listener = App.addListener('appUrlOpen', async (event: URLOpenListenerEvent) => {
      const url = event.url;
      persistentLog('[NativeAuth] App URL opened', url);
      
      // Check if this is an auth callback
      if (url.includes('auth/callback')) {
        await processAuthCallback(url);
      } else if (url.includes('auth/logout')) {
        persistentLog('[NativeAuth] Logout callback received');
        try {
          await Browser.close();
        } catch {
          // Ignore
        }
        clearNativeTokens();
        localStorage.removeItem('user');
        window.location.href = '/login';
      }
    });

    return () => {
      listener.then(l => l.remove()).catch(console.warn);
    };
  }, [exchangeCodeForTokens, processAuthCallback]);
}
